/**
 * 모달 하나. **셋이 같이 쓴다** — 티켓 상세 · 새 티켓 · 새 프로젝트.
 *
 * 껍데기를 화면마다 따로 쓰면 폭·여백·닫기 자리가 조금씩 갈린다.
 * 여기 하나만 고치면 셋이 함께 움직인다.
 *
 * **크기는 세 단계뿐이다.** 리터럴 폭을 부르는 쪽에서 새로 만들지 마라 —
 * 간격·글자와 같은 이유로, 자가 없으면 화면마다 다른 값이 쌓인다.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

/*
  ⚠ **폭의 주인은 여기 하나다.** shadcn 의 `dialog.tsx` 가 `sm:max-w-[560px]`
  를 들고 있어서 **`lg` 를 720 으로 적어 놓고도 실제로는 560 이 떴다**
  (2026-08-30 실측). 그 줄을 걷었다 — 다시 넣으면 이 표가 조용히 무시된다.
*/
const WIDTHS = {
  /** 폼 하나. 「새 프로젝트」처럼 칸이 두셋일 때. */
  sm: 'w-[min(440px,92vw)]',
  /** 기본. 「새 티켓」처럼 접었다 펴는 것이 있을 때. */
  md: 'w-[min(560px,92vw)]',
  /**
   * 티켓 상세처럼 속성이 열 줄 넘을 때. **속성이 두 벌로 선다**
   * (2026-08-30 지시) — 한 벌만 세우면 오른쪽이 통째로 비고 세로로 길어져
   * 아래쪽 속성을 보려면 스크롤해야 했다.
   */
  lg: 'w-[min(880px,92vw)]',
  /** 티켓 상세 — 본문 | 속성·이력 두 열(2026-10-18 C 현황판형). */
  /* 휴대폰에서는 화면을 꽉 채운다(2026-10-19) — 위 40px 틈으로 보드가
     비쳐 시트가 떠 보였고, 본문·속성·댓글을 읽기엔 높이가 모자랐다. */
  xl: 'w-[min(1040px,94vw)] max-[720px]:h-[100dvh] max-[720px]:max-h-[100dvh] max-[720px]:rounded-none',
} as const;

export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  frame = false,
  dismissOnOutside = true,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * 읽는 쪽에 주는 이름. **화면에는 안 그린다** — 내용에 이미 제목이 있는
   * 경우가 많고(티켓 상세), 그 위에 딱지를 하나 더 얹으면 같은 말이 두 번이다.
   * 눈에 보이는 제목이 필요하면 `children` 안에서 직접 그려라.
   */
  title: string;
  description?: string;
  size?: keyof typeof WIDTHS;
  /**
   * **머리와 아래 단추를 붙박이로 세운다**(2026-08-30 지시). 가운데만
   * 스크롤한다.
   *
   * 기본(`false`)은 모달 전체가 스크롤되는 옛 꼴이다 — 짧은 폼(새 티켓,
   * 프로젝트 만들기)은 그편이 맞다. 스크롤이 생길 만큼 길지 않아서,
   * 굳이 세 층으로 가르면 빈 머리와 빈 바닥만 남는다.
   *
   * ⚠ **켜면 안쪽 여백이 사라진다**(`p-0`). 세 층이 각자 자기 여백을
   * 지녀야 머리와 바가 모달 폭을 꽉 채울 수 있다 — 부르는 쪽이 그 셋을
   * 직접 그린다.
   */
  frame?: boolean;
  /** 편집 중 실수로 바깥을 탭해 초안이 사라지는 것을 막는다. */
  dismissOnOutside?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* 세로로 길어지면 모달 안이 스크롤된다 — 페이지가 밀리지 않게.
          `frame` 이면 그 스크롤을 가운데 층이 진다(위 주석). */}
      <DialogContent
        onInteractOutside={dismissOnOutside ? undefined : (event) => event.preventDefault()}
        className={
          frame
            ? `${WIDTHS[size]} flex max-h-[85vh] flex-col overflow-hidden p-0`
            : `${WIDTHS[size]} max-h-[85vh] overflow-y-auto`
        }
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {description && (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** 모달 안의 제목 줄. 눈에 보이는 제목이 필요할 때 쓴다. */
export function ModalTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mt-0 mb-s4 text-lead font-bold text-ink">{children}</h2>;
}
